import { useDroppable } from '@dnd-kit/core';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import TableCell from '@mui/material/TableCell';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { JobState, JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Observer, observer } from 'mobx-react-lite';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useFormContext } from 'react-hook-form';

import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable, { STICKY_CELL_Z_INDEX } from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { CELL_HORIZONTAL_PADDING_PX } from '~components/DataTable/getColumnSlots';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import SendTextDialog from '~components/Job/SendTextDialog';
import { DONE_JOB_STATES } from '~components/Maps/v2/constants';
import {
  HEADER_PANEL_Z_INDEX,
  LoadingSpinner,
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import OrderPricingHistoryButton from '~components/PricingHistory/OrderPricingHistoryButton';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { trackJobAssignmentCancelled } from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import JobCardPdfTypeDialog from '~hooks/useJobCardPdf/JobCardPdfTypeDialog';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { useJobCardPdf } from '~hooks/useJobCardPdf/useJobCardPdf';
import { Order } from '~hooks/useOrders/models';
import useEnsureOrderJobs from '~hooks/useOrders/useEnsureOrderJobs';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { hexToRgba } from '~utils/utilFunctions';

import { canAcceptJob } from '../utils';
import { ORDERS_DISPATCH_HEADER_ROW_HEIGHT_IN_PX } from './constants';
import {
  JobsTableColumnKey,
  jobsTableColumns,
  OrdersPresentationMode,
} from './constants';
import { EditJobTableRowsDto } from './editJobTableRowsSchema';
import { makeEditJobTableRowDto } from './editJobTableRowsUtils';
import { etaRowTintBgcolor } from './etaRowTint';
import JobDialog from './JobDialog';
import {
  ActionsColumn,
  CheckboxColumn,
  CycleTimeColumn,
  DeliveredColumn,
  getJobsTableColumns,
  GPSStatusColumn,
  JobNotesColumn,
  JobQuantityColumn,
  JobRatesColumn,
  JobRatesEditColumn,
  JobUnitOfMeasureColumn,
  makeJobsTableGridTemplateColumns,
  MaterialsColumn,
  RecurringLoadsColumn,
  RequestedColumn,
  RequestedEquipmentTypeColumn,
  StartColumn,
  StatusColumn,
  TruckAndTrailerColumn,
  VendorAndDriverColumn,
  WaypointsColumn,
} from './jobsTableColumns';
import JobsTableExpandedBlock from './JobsTableExpandedBlock';
import { Column, JobsTablePresentationContext } from './jobsTableStyledComponents';
import SplitJobDialog from './SplitJobDialog';

const NO_JOBS: Job[] = [];

const JobRowHoverContext = createContext(false);

type JobCellRenderers = Partial<
  Record<JobsTableColumnKey, (job: Job, isHovered: boolean) => ReactNode>
>;

enum JobActionDialog {
  CANCEL_JOB = 'cancel_job',
  DELETE_JOB = 'delete_job',
  FORM = 'form',
  SPLIT_JOB = 'split_job',
  TEXT_DRIVER = 'text_driver',
}

type JobActionDialogState = {
  dialog?: JobActionDialog;
  isOpen: boolean;
  job?: Job;
};

interface JobsTable {
  actionsContainer?: HTMLElement | null;
  checkedJobs?: string[];
  columnsMenuContainer?: HTMLElement | null;
  isEditingJobsTableRows?: boolean;
  isEditingOrderTrucksCount?: boolean;
  onCheckedStateChange?: (jobId: string) => void;
  onEditJobsTableRowsModeChange: (isEditingJobsTableRows: boolean) => void;
  onSubmitEditJobsTableRowsForm: () => void;
  order: Order;
  presentation?: OrdersPresentationMode;
  readOnly: boolean;
}

const JobsTable = observer(
  ({
    actionsContainer,
    checkedJobs,
    columnsMenuContainer,
    isEditingJobsTableRows,
    isEditingOrderTrucksCount,
    onCheckedStateChange,
    onEditJobsTableRowsModeChange,
    onSubmitEditJobsTableRowsForm,
    order,
    presentation = 'cards',
    readOnly,
  }: JobsTable) => {
    const theme = useTheme();
    const { ordersDispatchStore, toasterStore, userStore } = useStores();
    const {
      getJobsByOrder,
      cancelJob,
      sendJob,
      acceptJob,
      rejectJob,
      duplicateJob,
      deleteJob,
    } = useOrdersDispatch();
    const { getJobById } = useJob();
    const { generateJobCardPdf, generatingJobIds } = useJobCardPdf();
    const [expandedJobs, setExpandedJobs] = useState<string[]>([]);
    const [hoveredRow, setHoveredRow] = useState<string>();
    const [isLoadingJobsForEdit, setIsLoadingJobsForEdit] = useState(false);
    const [jobActionDialog, setJobActionDialog] = useState<JobActionDialogState>({
      isOpen: false,
    });
    const [pdfDialogJob, setPdfDialogJob] = useState<Job | null>(null);

    const companyId = getEffectiveUserCompanyId(userStore);

    const { canEditJob } = userStore.getPermissions();
    const canEditJobs = order.editable && canEditJob && !readOnly;

    const isLoadingJobsForDeepLinkedOrder =
      ordersDispatchStore.deepLinkedOrder?.id === order.id &&
      ordersDispatchStore.isDeepLinkedOrderNewlyCreated;
    const isLoadingJobsForOrder =
      (ordersDispatchStore.isLoadingOrderJobs.get(order.id) ?? true) &&
      !isLoadingJobsForEdit;
    const jobs = ordersDispatchStore.orderJobs.get(order.id) ?? NO_JOBS;

    const showDispatchJobsTruckAndTrailerColumnSettingEnabled =
      userStore.companySettings.isEnabled('show_dispatch_jobs_truck_and_trailer_column');
    const showDispatchJobsMaterialsColumnSettingEnabled =
      userStore.companySettings.isEnabled('show_dispatch_jobs_materials_column');
    const showDispatchJobsPickupDropoffColumnSettingEnabled =
      userStore.companySettings.isEnabled('show_dispatch_jobs_pickup_dropoff_column');
    const isLiveEtaEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.liveEtaEnabled,
    });

    const editJobsTableRowsForm = useFormContext<EditJobTableRowsDto>();

    useEffect(() => {
      if (editJobsTableRowsForm.formState.isSubmitting) {
        return;
      }

      const editTrucksSchemaDefaultValues = jobs.reduce((obj, job) => {
        obj[job.id] = makeEditJobTableRowDto(job, order);

        return obj;
      }, {} as EditJobTableRowsDto);

      if (!isEditingJobsTableRows) {
        editJobsTableRowsForm.reset(editTrucksSchemaDefaultValues);
      }
    }, [
      JSON.stringify(jobs),
      editJobsTableRowsForm.formState.isSubmitting,
      isEditingJobsTableRows,
      order.unitOfMeasure,
      order.quantity,
    ]);

    useEffect(() => {
      if (isEditingJobsTableRows) {
        setExpandedJobs([]);
      }
    }, [isEditingJobsTableRows]);

    const closeJobActionDialog = useCallback(
      (updatedJob?: Job) => {
        if (updatedJob instanceof Job) {
          ordersDispatchStore.upsertJob(updatedJob);
        }
        setJobActionDialog((state) => ({ ...state, isOpen: false }));
      },
      [ordersDispatchStore],
    );

    const openJobActionDialog = useCallback((dialog: JobActionDialog, job?: Job) => {
      setJobActionDialog((state) => ({ ...state, isOpen: true, dialog, job }));
    }, []);

    const handleExpandedStateChange = useCallback(
      (job: Job) => {
        if (isEditingJobsTableRows) {
          return;
        }

        setExpandedJobs((expandedJobs) => {
          const newExpandedJobs = [...expandedJobs];
          const index = newExpandedJobs.findIndex((jobId) => jobId === job.id);

          if (index > -1) {
            newExpandedJobs.splice(index, 1);
          } else {
            newExpandedJobs.push(job.id);
          }

          return newExpandedJobs;
        });
      },
      [isEditingJobsTableRows],
    );

    const handleAcceptJob = useCallback(
      async (jobId: string) => {
        try {
          const sentJob = await acceptJob(jobId, companyId);

          toasterStore.push(
            alert(t('dispatch.job.updated', { name: sentJob.jobId }), AlertTypes.success),
          );
        } catch {
          // Error is handled by the hook
        }
      },
      [acceptJob, companyId],
    );

    const handleCancelJob = useCallback(
      async (jobId: string) => {
        try {
          const cancelledJob = await cancelJob(jobId, companyId);
          trackJobAssignmentCancelled({
            action: JobStateEvent.CANCEL,
            jobIds: [jobId],
            source: 'actions_menu',
          });

          toasterStore.push(
            alert(
              t('dispatch.job.updated', { name: cancelledJob.jobId }),
              AlertTypes.success,
            ),
          );
          closeJobActionDialog();
        } catch {
          // Error is handled by the hook
        }
      },
      [cancelJob, closeJobActionDialog, companyId],
    );

    const handleDeleteJob = useCallback(
      async (jobId: string) => {
        try {
          await deleteJob(jobId);
          // toast is handled by OrdersDispatchStore removeJob method
          closeJobActionDialog();
        } catch {
          // Error is handled by the hook
        }
      },
      [closeJobActionDialog, deleteJob],
    );

    const handleDuplicateJob = useCallback(
      async (job: Job) => {
        try {
          const clonedJob = await duplicateJob(job.id);

          if (job.status === JobState.REJECTED) {
            await cancelJob(job.id, companyId);
          }

          toasterStore.push(
            alert(
              t('dispatch.job.copied', { name: clonedJob.jobId }),
              AlertTypes.success,
            ),
          );
        } catch {
          // Error is handled by the hook
        }
      },
      [cancelJob, companyId, duplicateJob],
    );

    const handleEditJob = useCallback(
      async (job: Job) => {
        try {
          // Fetch the latest job data before opening the dialog
          const latestJob = await getJobById(job.id, companyId);
          openJobActionDialog(JobActionDialog.FORM, latestJob);
        } catch (error) {
          console.error('Failed to fetch latest job data:', error);
          // Fallback to using the existing job data if fetch fails
          openJobActionDialog(JobActionDialog.FORM, job);
        }
      },
      [getJobById, openJobActionDialog, companyId],
    );

    const handleRejectJob = useCallback(
      async (jobId: string) => {
        try {
          const sentJob = await rejectJob(jobId, companyId);

          toasterStore.push(
            alert(t('dispatch.job.updated', { name: sentJob.jobId }), AlertTypes.success),
          );
        } catch {
          // Error is handled by the hook
        }
      },
      [companyId, rejectJob],
    );

    const handleSendJob = useCallback(
      async (jobId: string) => {
        try {
          const sentJob = await sendJob(jobId, companyId);

          toasterStore.push(
            alert(t('dispatch.job.updated', { name: sentJob.jobId }), AlertTypes.success),
          );
        } catch {
          // Error is handled by the hook
        }
      },
      [companyId, sendJob],
    );

    const handleViewJobCardPdf = useCallback((job: Job) => {
      setPdfDialogJob(job);
    }, []);

    const handlePdfTypeSelect = useCallback(
      (recipientType: JobCardPdfRecipientType) => {
        if (pdfDialogJob) {
          generateJobCardPdf(pdfDialogJob, recipientType);
        }
        setPdfDialogJob(null);
      },
      [generateJobCardPdf, pdfDialogJob],
    );

    useEnsureOrderJobs(order.id);

    const columns = useMemo(
      () =>
        getJobsTableColumns(
          Boolean(isEditingJobsTableRows),
          showDispatchJobsTruckAndTrailerColumnSettingEnabled,
          showDispatchJobsMaterialsColumnSettingEnabled,
          showDispatchJobsPickupDropoffColumnSettingEnabled,
        ),
      [
        isEditingJobsTableRows,
        showDispatchJobsMaterialsColumnSettingEnabled,
        showDispatchJobsPickupDropoffColumnSettingEnabled,
        showDispatchJobsTruckAndTrailerColumnSettingEnabled,
      ],
    );

    const jobCellRenderersRef = useRef<JobCellRenderers>({});

    const gridColumns = useMemo(
      () =>
        (Object.keys(columns) as JobsTableColumnKey[]).map(
          (key): DataTableColumn<Job> => {
            const { gridMinWidth, gridTitle, title } = jobsTableColumns[key];
            const minWidth =
              gridMinWidth === undefined
                ? undefined
                : gridMinWidth + 2 * CELL_HORIZONTAL_PADDING_PX;
            const renderCell = (job: Job) => (
              <JobGridCell
                renderContent={(isHovered) =>
                  jobCellRenderersRef.current[key]?.(job, isHovered)
                }
              />
            );

            if (key === JobsTableColumnKey.CHECKBOX) {
              return {
                field: key,
                headerName: t('data_table.select_row'),
                hideable: false,
                isHeaderNameHidden: true,
                minWidth,
                pin: 'left',
                resizable: false,
                renderCell,
              };
            }

            if (key === JobsTableColumnKey.ACTIONS) {
              return { ...actionsColumn(renderCell, key), minWidth };
            }

            return { field: key, headerName: gridTitle ?? title, minWidth, renderCell };
          },
        ),
      [columns],
    );

    const deepLinkedJobId = ordersDispatchStore.deepLinkedJobId;
    const isDeepLinkedJobListed =
      Boolean(deepLinkedJobId) && jobs.some((job) => job.id === deepLinkedJobId);

    useEffect(() => {
      if (deepLinkedJobId && isDeepLinkedJobListed) {
        setExpandedJobs((expandedJobs) =>
          expandedJobs.includes(deepLinkedJobId)
            ? expandedJobs
            : [...expandedJobs, deepLinkedJobId],
        );
        ordersDispatchStore.clearDeepLinkedJob();
      }
    }, [deepLinkedJobId, isDeepLinkedJobListed]);

    if (isLoadingJobsForDeepLinkedOrder) {
      return (
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: hexToRgba(theme.brandV2.colors.treadBlue, 0.1),
              border: `solid 1px ${hexToRgba(theme.brandV2.colors.treadBlue, 0.2)}`,
              borderRadius: theme.brandV2.borderRadius,
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              px: 1,
              py: 1.5,
            }}
          >
            <Box>
              <LoadingSpinner
                isVisible
                loadingIndicatorColor="info"
                loadingIndicatorSize={15}
              />
            </Box>

            <Typography variant="body2" color={theme.brandV2.colors.treadBlue}>
              {t('dispatch.dispatch_v2.jobs_are_being_created_message')}
            </Typography>
          </Box>
        </Box>
      );
    } else if (isLoadingJobsForOrder) {
      return <LoadingSpinner sx={{ my: 2 }} isVisible />;
    }

    const columnKeys = Object.keys(columns) as JobsTableColumnKey[];
    const isRowInteractionDisabled =
      isEditingJobsTableRows || userStore.isViewOnlyUser || readOnly;
    const gridTemplateColumns = makeJobsTableGridTemplateColumns(
      Boolean(isEditingJobsTableRows),
      showDispatchJobsTruckAndTrailerColumnSettingEnabled,
      showDispatchJobsMaterialsColumnSettingEnabled,
      showDispatchJobsPickupDropoffColumnSettingEnabled,
    );

    const jobCellRenderers: JobCellRenderers = {
      [JobsTableColumnKey.CHECKBOX]: (job: Job) => (
        <CheckboxColumn
          isChecked={checkedJobs?.includes(job.id)}
          job={job}
          onCheckedStateChange={() => onCheckedStateChange?.(job.id)}
          isDisabled={readOnly}
        />
      ),
      [JobsTableColumnKey.VENDOR_AND_DRIVER]: (job: Job, isHovered: boolean) => (
        <VendorAndDriverColumn
          filterDate={ordersDispatchStore.filters.startDate}
          isDisabled={isRowInteractionDisabled}
          isHovered={isHovered}
          job={job}
        />
      ),
      [JobsTableColumnKey.GPS_STATUS]: (job: Job) => <GPSStatusColumn job={job} />,
      [JobsTableColumnKey.REQUESTED_EQUIPMENT_TYPE]: (job: Job) => (
        <RequestedEquipmentTypeColumn job={job} order={order} />
      ),
      [JobsTableColumnKey.TRUCK_AND_TRAILER]: (job: Job) => (
        <TruckAndTrailerColumn job={job} />
      ),
      [JobsTableColumnKey.STATUS]: (job: Job) => (
        <StatusColumn
          isDisabled={isRowInteractionDisabled}
          job={job}
          onAccept={() => handleAcceptJob(job.id)}
          onReject={() => handleRejectJob(job.id)}
          onSend={() => handleSendJob(job.id)}
        />
      ),
      [JobsTableColumnKey.START]: (job: Job) => (
        <StartColumn isEditingJobsTableRows={isEditingJobsTableRows} job={job} />
      ),
      [JobsTableColumnKey.RECURRING_LOADS]: (job: Job) => (
        <RecurringLoadsColumn job={job} />
      ),
      [JobsTableColumnKey.CYCLE_TIME]: (job: Job) => <CycleTimeColumn job={job} />,
      [JobsTableColumnKey.REQUESTED]: (job: Job) => <RequestedColumn job={job} />,
      [JobsTableColumnKey.DELIVERED]: (job: Job) => <DeliveredColumn job={job} />,
      [JobsTableColumnKey.JOB_UNIT_OF_MEASURE]: (job: Job) => (
        <JobUnitOfMeasureColumn job={job} />
      ),
      [JobsTableColumnKey.JOB_QUANTITY]: (job: Job) => <JobQuantityColumn job={job} />,
      [JobsTableColumnKey.MATERIALS]: (job: Job) => (
        <MaterialsColumn isEditingJobsTableRows={isEditingJobsTableRows} job={job} />
      ),
      [JobsTableColumnKey.WAYPOINTS]: (job: Job) => <WaypointsColumn job={job} />,
      [JobsTableColumnKey.NOTES]: (job: Job) => (
        <JobNotesColumn isEditingJobsTableRows={isEditingJobsTableRows} job={job} />
      ),
      [JobsTableColumnKey.JOB_RATES]: (job: Job, isHovered: boolean) =>
        isEditingJobsTableRows ? (
          <JobRatesEditColumn job={job} order={order} />
        ) : (
          <JobRatesColumn isHovered={isHovered} job={job} order={order} />
        ),
      [JobsTableColumnKey.ACTIONS]: (job: Job, isHovered: boolean) =>
        readOnly ? null : (
          <ActionsColumn
            isHovered={isHovered}
            job={job}
            onCancel={() => openJobActionDialog(JobActionDialog.CANCEL_JOB, job)}
            onDelete={() => openJobActionDialog(JobActionDialog.DELETE_JOB, job)}
            onDuplicate={() => handleDuplicateJob(job)}
            onEdit={() => handleEditJob(job)}
            onSplit={() => openJobActionDialog(JobActionDialog.SPLIT_JOB, job)}
            onTextDriver={() => openJobActionDialog(JobActionDialog.TEXT_DRIVER, job)}
            onViewJobCardPdf={() => handleViewJobCardPdf(job)}
            isGeneratingJobCardPdf={generatingJobIds.has(job.id)}
            isOrderEditable={order.editable}
          />
        ),
    };
    jobCellRenderersRef.current = jobCellRenderers;

    const pricingHistoryButton = (
      <OrderPricingHistoryButton
        analyticsSurface="dispatch_rates_column"
        jobs={jobs}
        order={order}
      />
    );

    const editJobsTableRowsButton = (
      <SmallLoadingButton
        color="inherit"
        variant="outlined"
        loading={isLoadingJobsForEdit}
        onClick={async (event) => {
          event.stopPropagation();

          try {
            setIsLoadingJobsForEdit(true);
            await getJobsByOrder({
              orderId: order.id,
              companyId,
              clearExistingJobs: false,
            });

            // Lets the table re-render with the fetched jobs before the edit form reads them.
            await new Promise((resolve) => setTimeout(resolve, 100));

            setIsLoadingJobsForEdit(false);
            onEditJobsTableRowsModeChange(true);
          } catch (error) {
            console.error('Failed to fetch jobs:', error);
            setIsLoadingJobsForEdit(false);
            onEditJobsTableRowsModeChange(true);
          }
        }}
        sx={{
          '&.MuiButtonBase-root':
            presentation === 'grid'
              ? {
                  borderColor: theme.brandV2.colors.treadGray7,
                  height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                  px: 1,
                }
              : { px: 1, fontSize: '10px', height: '24px' },
        }}
      >
        {t('dispatch.order.edit_trucks')}
      </SmallLoadingButton>
    );

    const jobActionDialogs = (
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui={`page-dispatch__jobs__order-${order.orderId}__button`}
      >
        <JobDialog
          isOpen={
            jobActionDialog.dialog === JobActionDialog.FORM && jobActionDialog.isOpen
          }
          job={jobActionDialog.job}
          onClose={closeJobActionDialog}
        />

        <ConfirmationDialog
          isBusy={ordersDispatchStore.cancellingJobId === jobActionDialog.job?.id}
          isOpen={
            jobActionDialog.dialog === JobActionDialog.CANCEL_JOB &&
            jobActionDialog.isOpen
          }
          onClose={closeJobActionDialog}
          onConfirm={() => {
            if (jobActionDialog.job?.id) {
              handleCancelJob(jobActionDialog.job.id);
            }
          }}
          title={t('dispatch.job.cancel_job')}
        >
          <Typography>{t('dispatch.job.cancel_description')}</Typography>
        </ConfirmationDialog>

        <ConfirmationDialog
          isBusy={Boolean(ordersDispatchStore.deletingJobId)}
          isOpen={
            jobActionDialog.dialog === JobActionDialog.DELETE_JOB &&
            jobActionDialog.isOpen
          }
          onClose={closeJobActionDialog}
          onConfirm={() => {
            if (jobActionDialog.job?.id) {
              handleDeleteJob(jobActionDialog.job.id);
            }
          }}
          title={t('dispatch.job.delete_title')}
        >
          <Typography>{t('dispatch.job.delete_description')}</Typography>
        </ConfirmationDialog>

        <SendTextDialog
          isOpen={
            jobActionDialog.dialog === JobActionDialog.TEXT_DRIVER &&
            jobActionDialog.isOpen
          }
          mode="job_driver"
          source={jobActionDialog.job}
          onClose={closeJobActionDialog}
        />

        <JobCardPdfTypeDialog
          isOpen={pdfDialogJob !== null}
          onClose={() => setPdfDialogJob(null)}
          onSelect={handlePdfTypeSelect}
        />

        <SplitJobDialog
          isOpen={
            jobActionDialog.dialog === JobActionDialog.SPLIT_JOB && jobActionDialog.isOpen
          }
          job={jobActionDialog.job}
          onClose={closeJobActionDialog}
        />
      </Box>
    );

    if (presentation === 'grid') {
      return (
        <>
          {actionsContainer &&
            createPortal(
              <>
                {pricingHistoryButton}
                {canEditJobs && !isEditingOrderTrucksCount && editJobsTableRowsButton}
              </>,
              actionsContainer,
            )}
          <JobsTablePresentationContext.Provider value="grid">
            <DataTable
              columns={gridColumns}
              columnsMenuContainer={columnsMenuContainer}
              density="compact"
              expandedRowIds={expandedJobs}
              hideToolbar
              id="dispatch-grid-jobs-table"
              onExpandedRowIdsChange={setExpandedJobs}
              getRowColors={
                isLiveEtaEnabled
                  ? (job) => ({ tint: etaRowTintBgcolor(theme, job.eta) })
                  : undefined
              }
              onRowClick={handleExpandedStateChange}
              rowComponent={JobDroppableRow}
              renderDetailPanel={(job) => (
                <JobsTableExpandedBlock job={job} readOnly={readOnly} />
              )}
              rows={ordersDispatchStore.orderJobsSnapshotFor(order.id)}
              surfaceColor={theme.semantic.surfaceContainerHigh}
              sx={{ m: 1 }}
            />
          </JobsTablePresentationContext.Provider>
          {jobActionDialogs}
        </>
      );
    }

    return (
      <>
        <Box>
          <Box
            sx={{
              bgcolor: theme.brandV2.colors.treadGray10,
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'grid',
              gridAutoRows: `${ORDERS_DISPATCH_HEADER_ROW_HEIGHT_IN_PX}px`,
              gridTemplateColumns: gridTemplateColumns,
              position: 'sticky',
              px: 1,
              top: '-1px',
              zIndex: HEADER_PANEL_Z_INDEX - 1,
            }}
          >
            {Object.entries(columns).map(([key, column]) => (
              <Column key={key} columnKey={key as JobsTableColumnKey} isHeader>
                {key === JobsTableColumnKey.ACTIONS &&
                canEditJobs &&
                !isEditingOrderTrucksCount ? (
                  isEditingJobsTableRows ? (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <SmallButton
                        color="brandV2Red"
                        disabled={editJobsTableRowsForm.formState.isSubmitting}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditJobsTableRowsModeChange(false);
                        }}
                        sx={{
                          '&.MuiButtonBase-root': {
                            fontSize: '10px',
                            height: '24px',
                            px: 0.5,
                          },
                        }}
                      >
                        {t('actions.cancel')}
                      </SmallButton>

                      <SmallLoadingButton
                        color="brandV2Green"
                        loading={editJobsTableRowsForm.formState.isSubmitting}
                        onClick={(event) => {
                          event.stopPropagation();
                          onSubmitEditJobsTableRowsForm();
                        }}
                        sx={{
                          '&.MuiButtonBase-root': {
                            fontSize: '10px',
                            height: '24px',
                            px: 0.5,
                          },
                        }}
                      >
                        {t('actions.save')}
                      </SmallLoadingButton>
                    </Box>
                  ) : (
                    editJobsTableRowsButton
                  )
                ) : key === JobsTableColumnKey.JOB_RATES ? (
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                    <OverflowAwareText sx={{ fontWeight: 600 }}>
                      {column.title}
                    </OverflowAwareText>
                    {pricingHistoryButton}
                  </Box>
                ) : (
                  <OverflowAwareText fontWeight={600}>{column.title}</OverflowAwareText>
                )}
              </Column>
            ))}
          </Box>

          {jobs.map((job, index) => (
            <React.Fragment key={job.id}>
              <JobDroppableZone job={job} isLast={index === jobs.length - 1}>
                <Box
                  onClick={() => handleExpandedStateChange(job)}
                  onMouseEnter={() => setHoveredRow(job.id)}
                  onMouseLeave={() => setHoveredRow(undefined)}
                  data-ui={`page-dispatch__jobs__job-${job.jobId}__button`}
                  data-test-id={`page-dispatch__job-${job.jobId}__row`}
                  sx={{
                    bgcolor: isLiveEtaEnabled
                      ? etaRowTintBgcolor(theme, job.eta)
                      : undefined,
                    borderBottom:
                      index < jobs.length - 1
                        ? `solid 1px ${theme.brandV2.colors.treadGray7}`
                        : undefined,

                    display: 'grid',
                    gridTemplateColumns: gridTemplateColumns,
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {columnKeys.map((key) => (
                    <React.Fragment key={key}>
                      {jobCellRenderers[key]?.(job, hoveredRow === job.id)}
                    </React.Fragment>
                  ))}

                  {ordersDispatchStore.isUpdatingOrderJob.get(job.id) && (
                    <JobUpdatingOverlay />
                  )}
                </Box>
              </JobDroppableZone>

              <Collapse mountOnEnter unmountOnExit in={expandedJobs.includes(job.id)}>
                <Box
                  sx={{
                    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                  }}
                >
                  <JobsTableExpandedBlock job={job} readOnly={readOnly} />
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </Box>
        {jobActionDialogs}
      </>
    );
  },
);

type JobRowProps = TableRowProps & { row: Job };

interface JobDroppableZoneProps {
  isLast?: boolean;
  job: Job;
}

// Dropping an assignee here is the same assign the row's menu offers, so it needs the same
// entitlement check.
function useJobDropTarget(job: Job) {
  const theme = useTheme();
  const { userStore, ordersDispatchStore } = useStores();
  const { canAssignJob } = userStore.getPermissions();

  const { setNodeRef, isOver } = useDroppable({
    id: JSON.stringify({ jobId: job.id, orderId: String(job.order?.id) }),
  });

  const isJobAssignable = job.editable && canAssignJob && job.driverOrVendorAssignable;
  const isJobAcceptable = canAcceptJob(job);
  const isJobInDoneState = DONE_JOB_STATES.includes(job.status);

  const currentAssignee = job.driver ?? job.vendorJobAssignment?.vendorAccount;
  const newAssignee = ordersDispatchStore.draggedAssignee;

  const isJobAssigned = Boolean(currentAssignee);
  const isDragging = Boolean(ordersDispatchStore.draggedAssignee);

  const canChangeAssignee = isJobAcceptable
    ? false
    : isJobAssignable && !isJobAssigned && !isJobInDoneState;

  return {
    canChangeAssignee,
    dropSx: {
      bgcolor: isDragging ? theme.brandV2.colors.treadGray8 : 'transparent',
      ...(isDragging && isOver
        ? {
            outline: `dashed 2px ${theme.brandV2.colors.treadOrange}`,
            outlineOffset: '-2px',
          }
        : {}),
    },
    isOver,
    setNodeRef,
    title: t('dispatch.dispatch_v2.driver_panel.assign_job', {
      assignee:
        newAssignee instanceof DriverTypeahead ? newAssignee.fullName : newAssignee?.name,
      jobId: job.jobId,
    }),
  };
}

const JobDroppableZone = observer(
  ({ children, isLast, job }: React.PropsWithChildren<JobDroppableZoneProps>) => {
    const theme = useTheme();
    const { canChangeAssignee, dropSx, isOver, setNodeRef, title } =
      useJobDropTarget(job);

    if (canChangeAssignee) {
      return (
        <Tooltip arrow open={isOver} title={title}>
          <Box
            ref={setNodeRef}
            sx={{
              ...dropSx,
              ...(isLast
                ? {
                    borderBottomLeftRadius: theme.spacing(),
                    borderBottomRightRadius: theme.spacing(),
                  }
                : {}),
            }}
          >
            {children}
          </Box>
        </Tooltip>
      );
    }

    return children;
  },
);

const JobDroppableRow = observer(({ children, row: job, sx, ...props }: JobRowProps) => {
  const { ordersDispatchStore } = useStores();
  const { canChangeAssignee, dropSx, isOver, setNodeRef, title } = useJobDropTarget(job);
  const [isHovered, setIsHovered] = useState(false);

  const tableRow = (
    <TableRow
      {...props}
      data-test-id={`page-dispatch__job-${job.jobId}__row`}
      data-ui={`page-dispatch__jobs__job-${job.jobId}__button`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      ref={canChangeAssignee ? setNodeRef : undefined}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        { position: 'relative' },
        canChangeAssignee ? dropSx : {},
      ]}
    >
      <JobRowHoverContext.Provider value={isHovered}>
        {children}
      </JobRowHoverContext.Provider>
      {ordersDispatchStore.isUpdatingOrderJob.get(job.id) && (
        <TableCell
          sx={{
            border: 0,
            inset: 0,
            p: 0,
            position: 'absolute',
            zIndex: STICKY_CELL_Z_INDEX + 1,
          }}
        >
          <JobUpdatingOverlay />
        </TableCell>
      )}
    </TableRow>
  );

  return canChangeAssignee ? (
    <Tooltip arrow open={isOver} title={title}>
      {tableRow}
    </Tooltip>
  ) : (
    tableRow
  );
});

interface JobGridCellProps {
  renderContent: (isHovered: boolean) => ReactNode;
}

function JobGridCell({ renderContent }: JobGridCellProps) {
  const isHovered = useContext(JobRowHoverContext);

  return <Observer>{() => <>{renderContent(isHovered)}</>}</Observer>;
}

interface JobUpdatingOverlayProps {
  isLast?: boolean;
}

function JobUpdatingOverlay({ isLast }: JobUpdatingOverlayProps) {
  const theme = useTheme();

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        alignItems: 'center',
        display: 'flex',
        inset: 1,
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'absolute',
        ...(isLast
          ? {
              borderBottomLeftRadius: theme.spacing(),
              borderBottomRightRadius: theme.spacing(),
            }
          : {}),
      }}
      data-ui="page-dispatch__jobs__button"
    >
      <Box
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadGray10,
          inset: '0',
          opacity: 0.9,
          position: 'absolute',
        })}
      />
      <Box
        sx={{
          alignItems: 'center',
          display: ' flex',
          gap: 1,
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <LoadingSpinner isVisible loadingIndicatorSize={20} />

        <Typography sx={{ whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 500 }}>
          {t('dispatch.dispatch_v2.updating_job')}
        </Typography>
      </Box>
    </Box>
  );
}

export default JobsTable;
